"use client";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import PreviousButtonIcon from "../icons/PreviousButtonIcon";
import { useTripAudit } from "../../../contexts/TripAuditContext";
import axios from "axios";
import toast from "react-hot-toast";
import { AxiosErrorResponse } from "../../../interface/AxiosErrorResponse";
import LoadingButtonAnimation from "../../ui/shared/ButtonLoadingAnimation";
import { pricingPlans } from "../../../static_data/data";
import { formSchema } from "./TripAuditInspection";
import { CheckCircle, PhoneIcon } from "lucide-react";
import { Popover, Switch, Tooltip } from "antd";
import InfoIcon from "../icons/InfoIcon";
import z from "zod";
import ExpandedReportIcon from "../../../app/inspection/icons/ExpandedReportIcon";
import DriverPermitCardSkeleton from "../../../app/features/driver-license/components/DriverPermitCardSkeleton";
import CollapseIcon from "../../../app/inspection/[token]/result/Icons/CollapseIcon";
import apiConfig from "../../../lib/config";
type FormData = z.infer<typeof formSchema>;
const stampAccessPrices = {
  "90": 5,
  "180": 10,
  "365": 15,
} as const;

const LICENSE_VERIFICATION_PRICE = 4.99;
export default function InvoicePreCheckout() {
  const [isLoading, setIsLoading] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreeMarketing, setAgreeMarketing] = useState(false);
  const [showDriveInfo, setShowDriveInfo] = useState(true);
  const {
    watch,
    getValues,
    setValue,
    handleSubmit,
    reset,
    register,
    formState: { errors },
  } = useFormContext<FormData>();
  const { setShowInvoicePreCheckout, setShowInvoiceForm } = useTripAudit();
  const licenseVerification = watch("inspection.license_verification") ?? false;

  const toggleLicenseVerification = (checked: boolean) => {
    setValue("inspection.license_verification", checked, {
      shouldValidate: true,
    });
  };
  console.log(errors);
  const formData = watch();
  const planName = watch("inspection.plan") || "";
  const inspectionPlan = formData.inspection?.plan;
  const selectedPlan = pricingPlans.plans.find(
    (p) => p.value === inspectionPlan,
  );
  const planPrice = selectedPlan?.price ?? 0;
  const durations = watch("inspection.access_limit_time");

  const accessPrice =
    stampAccessPrices[durations as keyof typeof stampAccessPrices] ?? 0;
  const totalPrice =
    planPrice +
    accessPrice +
    (licenseVerification ? LICENSE_VERIFICATION_PRICE : 0);
  const toggleDuration = (duration: "90" | "180" | "365") => {
    const selectedDuration = getValues("inspection.access_limit_time");
    setValue(
      "inspection.access_limit_time",
      selectedDuration === duration ? undefined : duration,
    );
  };
  const onSubmit = async () => {
    if (!agreeTerms) return;

    setIsLoading(true);
    try {
      const payload = {
        // ... all the fields you already have in InvoiceSetupForm
        car_make: formData.vehicle.make,
        car_model: formData.vehicle.model,
        car_year: Number(formData.vehicle.year),
        fuel_type: formData.vehicle.fuelType,
        exterior_color: formData.vehicle.color || null,
        car_owner_name: formData.owner.fullName,
        car_owner_email: formData.owner.email,
        car_owner_phone: formData.owner.phone,
        car_owner_address: formData.owner.address || null,
        driver_name: formData.driver.sameAsOwner
          ? formData.owner.fullName
          : formData.driver.fullName,
        driver_email: formData.driver.sameAsOwner
          ? formData.owner.email
          : formData.driver.email,
        driver_phone: formData.driver.sameAsOwner
          ? formData.owner.phone
          : formData.driver.phone,
        driver_license: formData.driver.license || null,
        driver_address: formData.driver.address || null,
        inspection_type: inspectionPlan,
        access_limit_time: Number(
          formData.inspection.access_limit_time || "30",
        ),

        // Invoice specific fields
        invoice_currency: formData.invoice?.currency || "USD",
        is_primary_invoice: formData.invoice?.is_primary_invoice ?? true,
        trip_charge: formData.invoice?.trip_charge,
        security_deposit: formData.invoice?.security_deposit,
        is_trip_milage_limit: formData.invoice?.is_trip_milage_limit,
        trip_milage_limit: formData.invoice?.trip_milage_limit,
        excess_milage_fee: formData.invoice?.excess_milage_fee,
        is_late_return_fee: formData.invoice?.is_late_return_fee,
        return_datetime: formData.invoice?.return_datetime,
        late_return_fee: formData.invoice?.late_return_fee,
        is_additional_charges: formData.invoice?.is_additional_charges,
        is_verify_drivers_license: false,
        is_age_limit: false,
        standard_cleaning_charge: formData.invoice?.standard_cleaning_charge,
        fuel_level: formData.invoice?.fuel_level,
        payment_amount: planPrice,
        terms_and_conditions: agreeTerms,
        marketing: agreeMarketing,
        increment_payment_amount: accessPrice ?? 0,
        is_driver_permit: licenseVerification,
      };
      const orderResponse = await axios.post(
        `${apiConfig.api.baseUrl}/create_order`,
        payload,
        { headers: { "Content-Type": "application/json" } },
      );

      const tripId = orderResponse.data.trip_id;
      const customerEmail = formData.owner?.email ?? "";

      const createSessionData = {
        success_url: `https://drivestamp.com/payment-success?email=${customerEmail}`,
        cancel_url: "https://drivestamp.com/payment-failed",
      };

      const paymentResponse = await axios.post(
        `${apiConfig.api.baseUrl}/create_checkout_session?trip_id=${tripId}`,
        createSessionData,
      );
      if (paymentResponse?.data?.checkout_url) {
        reset();
        window.location.assign(paymentResponse.data.checkout_url);
      }
    } catch (error) {
      const axiosError = error as AxiosErrorResponse;
      const msg =
        axiosError?.response?.data?.error?.message || "Payment failed";
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };
  const scrollToTripwiseSection = () => {
    setTimeout(() => {
      document.getElementById("tripwise-section")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 50);
  };
  return (
    <div className="bg-white">
      <div className="max-w-6xl mx-auto p-5">
        <button
          onClick={() => {
            setShowInvoicePreCheckout(false);
            setShowInvoiceForm(true);
            scrollToTripwiseSection();
          }}
          className="flex items-center gap-2 cursor-pointer mb-5"
        >
          <PreviousButtonIcon />
          <span className="text-[14px] font-semibold text-[#999]">
            Previous
          </span>
        </button>
        <div className="bg-white rounded-md shadow-2xl overflow-hidden border border-gray-100">
          <div className="bg-[#FAFAFF] px-5 py-5 text-[#303030]">
            <h1 className="text-3xl font-bold">Your order info</h1>
            <p className="text-[#6F6464] mt-2">
              Review your order and proceed with the payment
            </p>
          </div>

          <div className="grid lg:grid-cols-5">
            {/* Left Column - Packages + Coverage Details */}
            <div className="lg:col-span-3 p-5 lg:border-r border-[#DDD] min-h-[650px]">
              <div className="border border-[#DDD] rounded-md  p-3 mb-5">
                <button
                  onClick={() => setShowDriveInfo(!showDriveInfo)}
                  className="w-full flex items-center justify-between "
                >
                  <div className=" mb-5">
                    <h3 className="text-[#303030] text-[20px] font-semibold text-left">
                      {formData.vehicle?.make} {formData.vehicle?.model}{" "}
                      {formData.vehicle?.year}
                    </h3>
                    <div className="flex space-x-2.5 items-center">
                      <p className="text-[#6F6464] text-[12px] font-medium leading-4">
                        VIN- {formData.vehicle?.vin || "N/A"}
                      </p>
                      <div className="bg-[#999] w-px h-4 opacity-30"></div>
                      <p className="text-[#6F6464] text-[12px] font-medium leading-4">
                        License-{formData.driver?.license || "N/A"}
                      </p>
                    </div>
                  </div>
                  {showDriveInfo ? <ExpandedReportIcon /> : <CollapseIcon />}
                </button>
                {showDriveInfo && (
                  <div>
                    {/* driver info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div className="border border-[#DFDFDF] bg-[#FAFAFA] rounded-[10px] p-4 mb-2.5">
                        <h3 className="text-[#333] text-[12px] leading-5 font-openSans font-bold mb-3">
                          Driver info
                        </h3>

                        <div className="flex items-center justify-between mb-3">
                          {/* Name */}
                          <div>
                            <p className="text-[14px] font-medium leading-[18px] text-[#333]">
                              {formData.driver?.fullName || "N/A"}
                            </p>
                            <p className="text-[12px] font-medium leading-4 text-[#6F6464]">
                              Name
                            </p>
                          </div>
                          {/* License */}
                          <div>
                            <p className="text-[14px] font-medium leading-[18px] text-[#333]">
                              DL-{formData.driver?.license || "N/A"}
                            </p>
                            <p className="text-[12px] font-medium leading-4 text-[#6F6464]">
                              Driver’s license
                            </p>
                          </div>
                        </div>

                        {/* Phone Number */}
                        <div className="flex items-center justify-between pt-3 border-t border-[#DFDFDF]">
                          <div>
                            <p className="text-[14px] font-medium leading-[18px] text-[#333]">
                              {formData.driver?.phone || "N/A"}
                            </p>
                            <p className="text-[12px] font-medium leading-4 text-[#6F6464]">
                              Phone number
                            </p>
                          </div>
                          {formData.driver?.phone && (
                            <a
                              href={`tel:${formData.driver.phone}`}
                              className="text-[#2D65F2]"
                            >
                              <PhoneIcon color="#2D65F2" size={16} />
                            </a>
                          )}
                        </div>
                      </div>
                      <div className="border border-[#B8CBFC] bg-[#F5F9FC] rounded-[10px] p-4 mb-2.5">
                        <h3 className="text-[#333] text-[12px] leading-5 font-openSans font-bold mb-3">
                          Owner info
                        </h3>

                        <div className="flex items-center justify-between mb-3">
                          {/* Name */}
                          <div>
                            <p className="text-[14px] font-medium leading-[18px] text-[#333]">
                              {formData.owner?.fullName || "N/A"}
                            </p>
                            <p className="text-[12px] font-medium leading-4 text-[#6F6464]">
                              Name
                            </p>
                          </div>
                          {/* License */}
                          <div>
                            <p className="text-[14px] font-medium leading-[18px] text-[#333]">
                              {/* DL-{driving_license || "Missing"} */}
                              DL-{formData.driver?.license || "N/A"}
                            </p>
                            <p className="text-[12px] font-medium leading-4 text-[#6F6464] font-inter">
                              Driver’s license
                            </p>
                          </div>
                        </div>

                        {/* Phone Number */}
                        <div className="flex items-center justify-between pt-3 border-t border-[#DFDFDF]">
                          <div>
                            <p className="text-[14px] font-medium leading-[18px] text-[#333]">
                              {formData.owner?.phone || "N/A"}
                            </p>
                            <p className="text-[12px] font-medium leading-4 text-[#6F6464]">
                              Phone number
                            </p>
                          </div>
                          {formData.owner?.phone && (
                            <a
                              href={`tel:${formData.owner.phone}`}
                              className="text-[#2D65F2]"
                            >
                              <PhoneIcon color="#2D65F2" size={16} />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Package Selection */}
              <h2 className="text-xl font-semibold text-[#04082C] mb-5">
                Selected Stamp Plan
              </h2>
              <div
                className={`p-5 mb-5 rounded-md border flex justify-between  cursor-pointer border-[#B8CBFC] bg-[#F5F9FC]  transition-all `}
              >
                <div>
                  <h3 className="font-bold text-[#04082C]">
                    {planName === "Invoice"
                      ? "Drive Invoice"
                      : planName === "Dual"
                        ? "Drive Audit"
                        : ""}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {" "}
                    {planName === "Invoice"
                      ? "Pre/Post Drive"
                      : planName === "Dual"
                        ? "Post-Trip Result"
                        : ""}
                  </p>
                </div>
                <p className="text-2xl font-bold text-[#04082C] mt-3">
                  ${planPrice.toFixed(2)}
                </p>
              </div>
              <div>
                {/* Interactive Stamp Access - Duration Cards */}
                <div className="flex items-center gap-2 mb-5">
                  <h2 className="text-xl font-semibold text-[#04082C] ">
                    Interactive Stamp Access
                  </h2>
                  <Tooltip
                    placement="top"
                    title={
                      "Select the duration for which you want to access your stamp.by default, it is set to 30 days."
                    }
                  >
                    <div className="flex  items-center">
                      <InfoIcon fill="#02636F" size={24} />
                    </div>
                  </Tooltip>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  {/* Card 1: 90 Days */}
                  <div
                    key="90"
                    className={`border rounded-xl p-5 cursor-pointer transition-all hover:shadow-md ${
                      durations === "90"
                        ? "border-[#2D65F2] bg-[#F5F9FF]"
                        : "border-[#DFDFDF] hover:border-gray-300"
                    }`}
                    onClick={(event) => {
                      if (event.target instanceof HTMLInputElement) return;
                      toggleDuration("90");
                    }}
                  >
                    <div className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="radio"
                        value="90"
                        checked={durations === "90"}
                        onChange={() => toggleDuration("90")}
                        className="mt-1 accent-[#2D65F2]"
                      />
                      <div className="flex-1">
                        <p
                          className={`text-[18px] font-bold font-openSans ${
                            durations === "90"
                              ? "text-[#151515]"
                              : "text-[#6F6464]"
                          }`}
                        >
                          90 Days
                        </p>
                        <p className="text-[#7D7D7D] text-sm mt-1">
                          Use within 90 days
                        </p>

                        <div className="mt-6">
                          <span className="text-3xl font-bold text-[#04082C]">
                            $5
                          </span>
                          <span className="text-[#6F6464] text-sm"> USD</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card 2: 180 Days */}
                  <div
                    key="180"
                    className={`border rounded-xl p-5 cursor-pointer transition-all hover:shadow-md ${
                      durations === "180"
                        ? "border-[#2D65F2] bg-[#F5F9FF]"
                        : "border-[#DFDFDF] hover:border-gray-300"
                    }`}
                    onClick={(event) => {
                      if (event.target instanceof HTMLInputElement) return;
                      toggleDuration("180");
                    }}
                  >
                    <div className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="radio"
                        value="180"
                        checked={durations === "180"}
                        onChange={() => toggleDuration("180")}
                        className="mt-1 accent-[#2D65F2]"
                      />
                      <div className="flex-1">
                        <p
                          className={`text-[18px] font-bold font-openSans ${
                            durations === "180"
                              ? "text-[#151515]"
                              : "text-[#6F6464]"
                          }`}
                        >
                          180 Days
                        </p>
                        <p className="text-[#7D7D7D] text-sm mt-1">
                          Use within 180 days
                        </p>

                        <div className="mt-6">
                          <span className="text-3xl font-bold text-[#04082C]">
                            $10
                          </span>
                          <span className="text-[#6F6464] text-sm"> USD</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card 3: 1 Year */}
                  <div
                    key="365"
                    className={`border rounded-xl p-5 cursor-pointer transition-all hover:shadow-md relative ${
                      durations === "365"
                        ? "border-[#2D65F2] bg-[#F5F9FF]"
                        : "border-[#DFDFDF] hover:border-gray-300"
                    }`}
                    onClick={(event) => {
                      if (event.target instanceof HTMLInputElement) return;
                      toggleDuration("365");
                    }}
                  >
                    <div className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="radio"
                        value="365"
                        checked={durations === "365"}
                        onChange={() => toggleDuration("365")}
                        className="mt-1 accent-[#2D65F2]"
                      />
                      <div className="flex-1">
                        <p
                          className={`text-[18px] font-bold font-openSans ${
                            durations === "365"
                              ? "text-[#151515]"
                              : "text-[#6F6464]"
                          }`}
                        >
                          1 Year
                        </p>
                        <p className="text-[#7D7D7D] text-sm mt-1">
                          Use within 365 days
                        </p>

                        <div className="mt-6">
                          <span className="text-3xl font-bold text-[#04082C]">
                            $15
                          </span>
                          <span className="text-[#6F6464] text-sm"> USD</span>
                        </div>
                      </div>
                    </div>

                    {/* Popular Badge */}
                    {/* <div className="absolute -top-2 -right-2 bg-[#2D65F2] text-white text-xs font-semibold px-3 py-1 rounded-full">
                    Best Value
                  </div> */}
                  </div>
                </div>
              </div>
              {/* Optional Driver License Verification */}
              <div className="border border-[#DDD] rounded-md p-5 mb-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold text-[#04082C]">
                        Driver Permit
                      </h3>
                      <Popover
                        placement="top"
                        trigger="hover"
                        overlayInnerStyle={{
                          padding: 0,
                          background: "transparent",
                          boxShadow: "none",
                        }}
                        content={
                          <div className="w-90">
                            <DriverPermitCardSkeleton />
                          </div>
                        }
                      >
                        <span className="inline-flex cursor-help">
                          <InfoIcon fill="#02636F" size={20} />
                        </span>
                      </Popover>
                    </div>
                    <p className="text-sm text-[#6F6464] leading-relaxed">
                      Get a dedicated verification URL in your portal to
                      securely process and validate the driver’s license.
                    </p>
                  </div>

                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <Switch
                      checked={licenseVerification}
                      onChange={toggleLicenseVerification}
                      className="scale-110"
                    />
                    {/* Show price only if it costs extra */}
                    {LICENSE_VERIFICATION_PRICE > 0 && (
                      <span className="text-3xl font-bold text-[#04082C] mt-1">
                        +${LICENSE_VERIFICATION_PRICE.toFixed(2)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Optional expanded info when enabled */}
                {licenseVerification && (
                  <div className="mt-4 pt-4 border-t border-[#DFDFDF]">
                    <div className="flex gap-3">
                      <CheckCircle className="w-5 h-5 text-[#2D65F2] flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-[#303030]">
                        After payment you will find a secure verification link
                        inside your DriveStamp portal to complete the driver
                        license check.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Trip Summary & Payment */}
            <div className="lg:col-span-2 bg-white p-5 flex flex-col max-h-[500px] ">
              <h3 className="font-semibold text-lg mb-6 text-[#04082C]">
                Order Summary
              </h3>

              <div className="space-y-4 text-sm mb-10 text-[#303030]">
                <div className="flex justify-between">
                  <span className="text-gray-500">Stamp Plan</span>
                  <span className="font-medium">
                    {planName === "Invoice"
                      ? "Drive Invoice"
                      : planName === "Dual"
                        ? "Drive Audit"
                        : ""}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Stamp Access Durations</span>
                  <span className="font-medium">
                    {formData.inspection?.access_limit_time || "30"} days
                  </span>
                </div>
                {licenseVerification && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Driver Permit</span>
                    <span className="font-medium">
                      {LICENSE_VERIFICATION_PRICE > 0
                        ? `+$${LICENSE_VERIFICATION_PRICE.toFixed(2)}`
                        : "Included"}
                    </span>
                  </div>
                )}
              </div>
              {/* <div className="space-y-6 mb-4">
              <div className="border rounded-lg p-4 bg-[#F9FAFB] text-gray-500 space-y-1.5">
                <h3 className="font-semibold text-lg mb-4 text-[#04082C]">Primary Invoice</h3>
                <p className="flex justify-between items-center">Trip Charge: <strong>${formData.invoice?.trip_charge || 0}</strong></p>
                <p className="flex justify-between items-center">Security Deposit: <strong>${formData.invoice?.security_deposit || 0}</strong></p>
              </div>

              {formData.invoice?.is_trip_milage_limit && (
                <div className="border rounded-lg p-4 bg-[#F9FAFB]">
                  <h3 className="font-semibold mb-3">Mileage Limit</h3>
                  <p>Limit: {formData.invoice.trip_milage_limit} km/miles</p>
                  <p>Excess Fee: ${formData.invoice.excess_milage_fee}/km</p>
                </div>
              )}

            </div> */}
              {/* Terms & Conditions Checkbox */}
              <div className="mb-6 space-y-4">
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={agreeTerms}
                    onChange={(e) => setAgreeTerms(e.target.checked)}
                    className="mt-0.5 w-5 h-5 accent-[#2D65F2] border-2 border-gray-300 rounded cursor-pointer 
                   "
                  />
                  <span className="text-sm text-gray-600 leading-relaxed">
                    I agree to the{" "}
                    <a
                      href="/terms-of-service"
                      target="_blank"
                      className="text-[#04082C] hover:underline font-medium"
                    >
                      Terms & Conditions
                    </a>{" "}
                    and{" "}
                    <a
                      href="/privacy-policy"
                      target="_blank"
                      className="text-[#04082C] hover:underline font-medium"
                    >
                      Privacy Policy
                    </a>
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer group">
                  <div>
                    <input
                      type="checkbox"
                      checked={agreeMarketing}
                      onChange={(e) => setAgreeMarketing(e.target.checked)}
                      className="mt-0.5 w-5 h-5 accent-[#2D65F2] border-2 border-gray-300 rounded cursor-pointer"
                    />
                  </div>
                  <span className="text-sm text-gray-600 leading-relaxed">
                    I agree to receive{" "}
                    <span className="text-[#04082C] font-medium">
                      Marketing Emails
                    </span>{" "}
                    from Drive Stamp.
                  </span>
                </label>
              </div>

              {/* Price Box */}
              <div className="mt-auto bg-white rounded-md p-5 shadow-[0_2px_5px_0_rgba(0,0,0,0.05)]">
                <div className="text-right">
                  {/* {isIncluded && (
                  <span className="text-gray-400 line-through block">
                    US ${originalTotal}
                  </span>
                )} */}
                  <div className="text-[28px] font-bold text-[#04082C] mt-1">
                    US ${totalPrice ? totalPrice.toFixed(2) : "0"}
                  </div>
                  {/* {isIncluded && (
                  <p className="text-[#4DB429] font-medium mt-2">
                    You saved US ${(originalTotal - totalPrice).toFixed(2)} (
                    {Math.round(
                      ((originalTotal - totalPrice) / originalTotal) * 100,
                    )}
                    %)
                  </p>
                )} */}
                </div>

                {/* Proceed Button - Disabled until checkbox is checked */}
                <button
                  onClick={handleSubmit(onSubmit, (errors) => {
                    console.log("Validation Errors");
                    console.log(errors);
                  })}
                  disabled={!agreeTerms}
                  className={`w-full h-12 font-bold rounded-md transition-all duration-200 cursor-pointer mt-5
        ${
          agreeTerms
            ? "submit-button"
            : "bg-[#DDD] text-gray-500 cursor-not-allowed"
        }`}
                >
                  {isLoading ? (
                    <LoadingButtonAnimation />
                  ) : (
                    "PROCEED TO PAYMENT"
                  )}
                </button>

                {/* <buttoncle
                onClick={() => setEditDurations(true)}
                className="w-full border capitalize border-[#ddd] text-[#04082C] font-semibold py-4 rounded-md mt-3 hover:bg-gray-50 transition-colors"
              >
                Edit Durations
              </button> */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
